import { Stack, Text, Title, useMantineTheme } from "@mantine/core"
import {
  CommunicationStudioClasses,
  CUSTOMISATION_CATEGORIES,
} from "@tfg/types/constants/communication-studio.constant"
import {
  AGECResponses,
  RecycledMaterialPercentageCause,
} from "@tfg/types/endpoints/agec"
import { TextTransform } from "@tfg/types/schemas/communication-studio/cssStylesSchema"
import {
  contentVerticalSpacingCSSVariable,
  headerFontFamilyCSSVariable,
  headerFontWeightCSSVariable,
  headerLineHeightCSSVariable,
  headerTextDecorationCSSVariable,
  textColorCssVariable,
  textFontSizeCSSVariable,
  textFontWeightCSSVariable,
  titleColorCSSVariable,
} from "@tfg/types/schemas/communication-studio/cssVariables"

import { useGetStylingParametersContext } from "../context/stylingParameters/useGetStylingParametersContext"
import { useGetTranslationsContext } from "../context/translationsContext/useGetTranslationsContext"
import { Scan4ChemAGEC } from "../page-components/Scan4ChemText"
import { DesignStudioParametersToolkit } from "../utils/designStudioParametersToolkit"

interface Props {
  agecData: AGECResponses.GenericAgecData
}

export const RecycledFiberAndMicroPlastics = ({ agecData }: Props) => {
  const { t } = useGetTranslationsContext()
  const theme = useMantineTheme()
  const { stylingParameters } = useGetStylingParametersContext()

  if (!stylingParameters) return null

  const hasRecycledFibersAmount =
    agecData.recycledFibers.percentage &&
    agecData.recycledFibers.cause !==
      RecycledMaterialPercentageCause.NOT_CALCULATED &&
    agecData.recycledFibers.percentage !== 0

  return (
    <Stack
      ff={theme.fontFamily}
      gap={DesignStudioParametersToolkit.getCssVariableLiteralName(
        contentVerticalSpacingCSSVariable
      )}
      c={DesignStudioParametersToolkit.getCssVariableLiteralName(
        textColorCssVariable
      )}
    >
      <Title
        order={3}
        c={DesignStudioParametersToolkit.getCssVariableLiteralName(
          titleColorCSSVariable
        )}
        style={DesignStudioParametersToolkit.spreadStylingParameters({
          category: CUSTOMISATION_CATEGORIES.GLOBAL,
          stylingParameters,
          stylingParamKeys: ["letterSpacing"],
        })}
        lh={DesignStudioParametersToolkit.getCssVariableLiteralName(
          headerLineHeightCSSVariable
        )}
        td={DesignStudioParametersToolkit.getCssVariableLiteralName(
          headerTextDecorationCSSVariable
        )}
        fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
          headerFontWeightCSSVariable
        )}
        tt={
          DesignStudioParametersToolkit.extractValueFromCssVariable(
            stylingParameters,
            CUSTOMISATION_CATEGORIES.GLOBAL,
            "headerTextTransform"
          ) as TextTransform | undefined
        }
        ff={`${DesignStudioParametersToolkit.getCssVariableLiteralName(
          headerFontFamilyCSSVariable
        )}, ${DesignStudioParametersToolkit.extractFontFamily(stylingParameters)}
           `}
        className={CommunicationStudioClasses.PACKAGING_INFO_HEADER}
      >
        {t("other.title")}
      </Title>

      <Stack gap={0}>
        {hasRecycledFibersAmount ? (
          <Text
            c={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textColorCssVariable
            )}
            fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontWeightCSSVariable
            )}
            fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontSizeCSSVariable
            )}
            style={DesignStudioParametersToolkit.spreadStylingParameters({
              category: CUSTOMISATION_CATEGORIES.GLOBAL,
              stylingParameters,
              stylingParamKeys: ["letterSpacing"],
            })}
          >
            {agecData.recycledFibers.percentage === 100 ? (
              t("other.paragraph1_100")
            ) : (
              <>
                {t("other.paragraph1")}
                <Text
                  component="span"
                  className={
                    CommunicationStudioClasses.RECYCLED_PACKAGING_KEY_INFO
                  }
                  fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
                    textFontSizeCSSVariable
                  )}
                  style={DesignStudioParametersToolkit.spreadStylingParameters({
                    category: CUSTOMISATION_CATEGORIES.GLOBAL,
                    stylingParameters,
                    stylingParamKeys: ["letterSpacing"],
                  })}
                >
                  {t("other.recycled_matertials_bold", {
                    percent:
                      agecData.recycledFibers.percentage?.toString() ?? "",
                  })}
                </Text>
              </>
            )}
          </Text>
        ) : null}
        {agecData.releasesMicroPlastic && (
          <Text
            c={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textColorCssVariable
            )}
            fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontWeightCSSVariable
            )}
            fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontSizeCSSVariable
            )}
            style={DesignStudioParametersToolkit.spreadStylingParameters({
              category: CUSTOMISATION_CATEGORIES.GLOBAL,
              stylingParameters,
              stylingParamKeys: ["letterSpacing"],
            })}
          >
            {t("other.paragraph2_1")}
            <Text
              component="span"
              className={CommunicationStudioClasses.RECYCLED_PACKAGING_KEY_INFO}
              fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
                textFontSizeCSSVariable
              )}
              style={DesignStudioParametersToolkit.spreadStylingParameters({
                category: CUSTOMISATION_CATEGORIES.GLOBAL,
                stylingParameters,
                stylingParamKeys: ["letterSpacing"],
              })}
            >
              {t("other.microplastics_bold")}
            </Text>
            {t("other.paragraph2_2")}
            <Text
              component="span"
              className={CommunicationStudioClasses.RECYCLED_PACKAGING_KEY_INFO}
              fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
                textFontSizeCSSVariable
              )}
              style={DesignStudioParametersToolkit.spreadStylingParameters({
                category: CUSTOMISATION_CATEGORIES.GLOBAL,
                stylingParameters,
                stylingParamKeys: ["letterSpacing"],
              })}
            >
              {t("other.recycled_matertials_bold_50")}
            </Text>
            {t("other.paragraph2_3")}
          </Text>
        )}
        <Scan4ChemAGEC />
      </Stack>
    </Stack>
  )
}
